import React, { useState, useRef } from 'react';
import { toast } from '../utils/toast';
import { socialMediaService } from '../services/socialMediaService';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';
import DeploymentOptions from '../components/DeploymentOptions';

export function SocialMediaManager() {
  const [postInput, setPostInput] = useState('');
  const [brandVoice, setBrandVoice] = useState('professional');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['linkedin']);
  const [contentType, setContentType] = useState('thought_leadership');
  const [generatedPosts, setGeneratedPosts] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [targetAudience, setTargetAudience] = useState('');
  const [keyObjectives, setKeyObjectives] = useState([]);
  const [customHashtags, setCustomHashtags] = useState('');
  const [connectedAccounts, setConnectedAccounts] = useState({
    linkedin: { connected: false, username: '' },
    facebook: { connected: false, username: '' },
    instagram: { connected: false, username: '' },
    twitter: { connected: false, username: '' },
    reddit: { connected: false, username: '' }
  });
  const [autoPost, setAutoPost] = useState(false);
  const [scheduledTime, setScheduledTime] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const platformOptions = [
    { 
      value: 'linkedin', 
      label: 'LinkedIn', 
      icon: 'M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z M2 4a2 2 0 1 1 4 0 2 2 0 1 1-4 0'
    },
    { 
      value: 'facebook', 
      label: 'Facebook', 
      icon: 'M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14c-.326-.043-1.557-.14-2.857-.14C11.928 2 10 3.657 10 6.7v2.8H7v4h3V22h4v-8.5z'
    },
    { 
      value: 'instagram', 
      label: 'Instagram', 
      icon: 'M12 2c2.717 0 3.056.01 4.122.06 1.065.05 1.79.217 2.428.465.66.254 1.216.598 1.772 1.153a4.908 4.908 0 0 1 1.153 1.772c.247.637.415 1.363.465 2.428.047 1.066.06 1.405.06 4.122 0 2.717-.01 3.056-.06 4.122-.05 1.065-.218 1.79-.465 2.428a4.883 4.883 0 0 1-1.153 1.772 4.915 4.915 0 0 1-1.772 1.153c-.637.247-1.363.415-2.428.465-1.066.047-1.405.06-4.122.06-2.717 0-3.056-.01-4.122-.06-1.065-.05-1.79-.218-2.428-.465a4.89 4.89 0 0 1-1.772-1.153 4.904 4.904 0 0 1-1.153-1.772c-.248-.637-.415-1.363-.465-2.428C2.013 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122.05-1.066.217-1.79.465-2.428a4.88 4.88 0 0 1 1.153-1.772A4.897 4.897 0 0 1 5.45 2.525c.638-.248 1.362-.415 2.428-.465C8.944 2.013 9.283 2 12 2zm0 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm6.5-.25a1.25 1.25 0 1 0-2.5 0 1.25 1.25 0 0 0 2.5 0zM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z'
    },
    { 
      value: 'twitter', 
      label: 'X (Twitter)', 
      icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z'
    },
    { 
      value: 'reddit', 
      label: 'Reddit', 
      icon: 'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm-2.5-5.5c.828 0 1.5-.672 1.5-1.5s-.672-1.5-1.5-1.5-1.5.672-1.5 1.5.672 1.5 1.5 1.5zm5 0c.828 0 1.5-.672 1.5-1.5s-.672-1.5-1.5-1.5-1.5.672-1.5 1.5.672 1.5 1.5 1.5zm2.5-4c0-1.5-1.567-2.5-3.5-2.5s-3.5 1-3.5 2.5 1.567 2.5 3.5 2.5 3.5-1 3.5-2.5z'
    }
  ];

  const voiceOptions = [
    { value: 'conversational', label: 'Conversational & Authentic' },
    { value: 'friendly', label: 'Warm & Friendly' },
    { value: 'expert', label: 'Expert & Approachable' },
    { value: 'storyteller', label: 'Engaging Storyteller' },
    { value: 'community', label: 'Community-Focused' }
  ];

  const contentTypeOptions = [
    { value: 'story', label: 'Personal Story or Experience' },
    { value: 'insight', label: 'Helpful Insight or Tip' },
    { value: 'discussion', label: 'Conversation Starter' },
    { value: 'behind_scenes', label: 'Behind the Scenes' },
    { value: 'celebration', label: 'Team or Customer Celebration' },
    { value: 'learning', label: 'Learning or Growth Moment' }
  ];

  const objectiveOptions = [
    { value: 'engagement', label: 'Drive Engagement' },
    { value: 'awareness', label: 'Raise Awareness' },
    { value: 'leads', label: 'Generate Leads' },
    { value: 'traffic', label: 'Drive Website Traffic' },
    { value: 'authority', label: 'Build Authority' },
    { value: 'community', label: 'Build Community' }
  ];

  const handleObjectiveToggle = (objective) => {
    setKeyObjectives(prev => {
      if (prev.includes(objective)) {
        return prev.filter(obj => obj !== objective);
      }
      return [...prev, objective];
    });
  };

  const handlePlatformToggle = (platformValue) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platformValue)) {
        return prev.filter(p => p !== platformValue);
      }
      return [...prev, platformValue];
    });
  };

  const handleVoiceTranscriptionComplete = (transcribedText) => {
    setPostInput(transcribedText);
  };

  const handleConnect = (platform) => {
    // Simulate OAuth flow
    toast.info(`Connecting to ${platform}...`);
    setTimeout(() => {
      setConnectedAccounts(prev => ({
        ...prev,
        [platform]: {
          connected: true,
          username: `demo_${platform}_user`
        }
      }));
      toast.success(`Successfully connected to ${platform}!`);
    }, 1500);
  };

  const handleDisconnect = (platform) => {
    setConnectedAccounts(prev => ({
      ...prev,
      [platform]: {
        connected: false,
        username: ''
      }
    }));
    toast.success(`Disconnected from ${platform}`);
  };

  const simulatePosting = async (post, platform) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 1000);
    });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Simulate upload progress
      setIsUploading(true);
      setUploadProgress(0);
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsUploading(false);
            toast.success('Image uploaded successfully!');
            return 100;
          }
          return prev + 10;
        });
      }, 200);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const generatePost = async () => {
    if (!postInput.trim()) {
      toast.error('Please provide content details');
      return;
    }

    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    setIsGenerating(true);
    try {
      // Simulate AI processing steps with progress notifications
      toast.info('Analyzing content and optimizing for selected platforms...');
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast.info('Generating platform-specific variations...');
      await new Promise(resolve => setTimeout(resolve, 1200));

      if (selectedImage) {
        toast.info('Processing and optimizing image for each platform...');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      const newPosts = [];

      for (const platform of selectedPlatforms) {
        toast.info(`Generating optimized content for ${platform}...`);
        await new Promise(resolve => setTimeout(resolve, 800));

        const response = await socialMediaService.generateContent({
          postInput,
          brandVoice,
          platform,
          contentType,
          targetAudience,
          keyObjectives,
          customHashtags,
          hasImage: !!selectedImage
        });
        
        if (response.error) {
          toast.error(`Error generating ${platform} post: ${response.error}`);
        } else {
          newPosts.push({
            id: Date.now() + Math.random(),
            platform: platform,
            content: response.content,
            image: imagePreview,
            timestamp: new Date().toISOString(),
            status: autoPost ? 'pending' : 'draft',
            analytics: {
              estimatedReach: Math.floor(Math.random() * 10000) + 1000,
              engagementRate: (Math.random() * 5 + 1).toFixed(2),
              bestTimeToPost: new Date(Date.now() + Math.random() * 86400000).toLocaleTimeString()
            }
          });
        }
      }

      if (newPosts.length > 0) {
        setGeneratedPosts([...newPosts, ...generatedPosts]);
        toast.success(`Generated authentic posts for ${newPosts.length} platform(s)!`);

        if (autoPost) {
          setIsPosting(true);
          for (const post of newPosts) {
            if (connectedAccounts[post.platform].connected) {
              try {
                toast.info(`Preparing to post on ${post.platform}...`);
                await new Promise(resolve => setTimeout(resolve, 800));
                
                toast.info(`Optimizing media for ${post.platform}...`);
                await new Promise(resolve => setTimeout(resolve, 600));
                
                await simulatePosting(post, post.platform);
                post.status = 'posted';
                toast.success(`Successfully posted to ${post.platform}!`);
                
                // Simulate engagement updates
                setTimeout(() => {
                  post.analytics.likes = Math.floor(Math.random() * 100);
                  post.analytics.shares = Math.floor(Math.random() * 50);
                  post.analytics.comments = Math.floor(Math.random() * 20);
                  setGeneratedPosts([...generatedPosts]);
                }, 3000);
              } catch (error) {
                post.status = 'failed';
                toast.error(`Failed to post to ${post.platform}`);
              }
            }
          }
          setIsPosting(false);
        }
      }
    } catch (error) {
      console.error('Error generating posts:', error);
      toast.error('Failed to generate posts');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  const quickStartGuides = [
    {
      title: "Content Strategy",
      description: "Define your message and choose your target platforms. Set your brand voice and content type to ensure consistent messaging.",
      keywords: ["Message", "Platforms", "Brand Voice"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      )
    },
    {
      title: "AI Generation",
      description: "Let our AI create platform-specific content that resonates with your audience while maintaining your brand identity.",
      keywords: ["AI Writing", "Platform Optimization", "Engagement"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: "Review & Schedule",
      description: "Review your generated posts, make any final adjustments, and prepare them for publishing across your platforms.",
      keywords: ["Review", "Schedule", "Publish"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    }
  ];

  const imageUploadSection = (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Add Image
      </label>
      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
        <div className="space-y-1 text-center">
          {!imagePreview ? (
            <>
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                >
                  <span>Upload a file</span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    ref={fileInputRef}
                    className="sr-only"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
            </>
          ) : (
            <div className="relative">
              <img
                src={imagePreview}
                alt="Preview"
                className="max-h-48 rounded-lg"
              />
              <button
                onClick={removeImage}
                className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      {isUploading && (
        <div className="mt-2">
          <div className="bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-2 bg-primary-600 transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1 text-center">
            Uploading... {uploadProgress}%
          </p>
        </div>
      )}
    </div>
  );

  const renderPost = (post) => (
    <div key={post.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <svg 
            className={`w-5 h-5 ${
              post.status === 'posted' 
                ? 'text-primary-600' 
                : post.status === 'failed'
                ? 'text-red-500'
                : 'text-gray-400'
            }`} 
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d={platformOptions.find(p => p.value === post.platform)?.icon} />
          </svg>
          <span className="font-medium text-gray-900">
            {platformOptions.find(p => p.value === post.platform)?.label}
          </span>
          <span className={`text-xs px-2 py-1 rounded-full ${
            post.status === 'posted' 
              ? 'bg-green-100 text-green-800'
              : post.status === 'failed'
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {post.status === 'posted' ? 'Posted' : post.status === 'failed' ? 'Failed' : 'Draft'}
          </span>
        </div>
        <button
          onClick={() => copyToClipboard(post.content)}
          className="text-gray-500 hover:text-gray-700"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>
      </div>
      <div className="prose max-w-none">
        <div className="whitespace-pre-wrap">{post.content}</div>
        {post.image && (
          <div className="mt-4">
            <img src={post.image} alt="Post media" className="rounded-lg max-h-64 w-auto" />
          </div>
        )}
      </div>
      <div className="mt-4 text-sm text-gray-500">
        Generated {new Date(post.timestamp).toLocaleString()}
      </div>
      {post.status === 'posted' && post.analytics && (
        <div className="mt-4 grid grid-cols-3 gap-4 bg-white p-4 rounded-lg">
          <div className="text-center">
            <div className="text-xl font-semibold text-primary-600">{post.analytics.likes || 0}</div>
            <div className="text-xs text-gray-500">Likes</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold text-primary-600">{post.analytics.shares || 0}</div>
            <div className="text-xs text-gray-500">Shares</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold text-primary-600">{post.analytics.comments || 0}</div>
            <div className="text-xs text-gray-500">Comments</div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern SaaS Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          {/* Modern grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          
          {/* Top and bottom subtle lines */}
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
          
          {/* Glowing orbs */}
          <div className="absolute -left-48 -top-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-500/40 via-primary-300/10 to-primary-400/40 blur-xl opacity-30"></div>
          </div>
          <div className="absolute -right-48 -bottom-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-400/40 via-primary-300/10 to-primary-500/40 blur-xl opacity-30"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary-800/50 border border-primary-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></div>
                <span className="text-sm text-primary-100">AI-Powered Content</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Social Media Manager
              </h1>
              <p className="text-lg text-primary-200 max-w-xl">
                Create, schedule, and manage your social media content with AI assistance
              </p>
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center space-x-2 text-sm text-primary-200">
                  <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Multi-Platform</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-primary-200">
                  <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Smart Scheduling</span>
                </div>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <div className="relative group">
                <div className="absolute -inset-1 rounded-lg bg-gradient-to-r from-primary-400/20 via-primary-300/20 to-primary-500/20 blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                <div className="relative flex items-center space-x-4 bg-primary-800/50 backdrop-blur-xl px-6 py-4 rounded-xl border border-primary-700/50">
                  <div className="flex flex-col items-center justify-center">
                    <div className="text-2xl font-bold text-white">5+</div>
                    <div className="text-xs text-primary-300">Platforms</div>
                  </div>
                  <div className="w-px h-12 bg-primary-700/50"></div>
                  <div className="flex flex-col items-center justify-center">
                    <div className="text-2xl font-bold text-primary-400">24/7</div>
                    <div className="text-xs text-primary-300">Scheduling</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 mb-8">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Connected Accounts</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {platformOptions.filter(p => p.value !== 'blog').map((platform) => (
              <div key={platform.value} className="flex flex-col items-center p-4 border rounded-lg">
                <svg 
                  className={`w-8 h-8 mb-2 ${
                    connectedAccounts[platform.value].connected 
                      ? 'text-primary-600' 
                      : 'text-gray-400'
                  }`} 
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d={platform.icon} />
                </svg>
                <span className="text-sm font-medium mb-2">{platform.label}</span>
                {connectedAccounts[platform.value].connected ? (
                  <>
                    <span className="text-xs text-gray-500 mb-2">
                      @{connectedAccounts[platform.value].username}
                    </span>
                    <button
                      onClick={() => handleDisconnect(platform.value)}
                      className="text-xs px-3 py-1 bg-red-50 text-red-600 rounded-full hover:bg-red-100"
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleConnect(platform.value)}
                    className="text-xs px-3 py-1 bg-primary-50 text-primary-600 rounded-full hover:bg-primary-100"
                  >
                    Connect
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Input */}
            <div className="space-y-6">
              {/* Post Input with Voice Recording */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Post Content
                </label>
                <VoiceRecorder 
                  onTranscriptionComplete={handleVoiceTranscriptionComplete}
                  placeholder="Click to record your post content..."
                  className="mb-4"
                />
                <textarea
                  value={postInput}
                  onChange={(e) => setPostInput(e.target.value)}
                  rows="4"
                  placeholder="Record or type your post content..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Image Upload Section */}
              {imageUploadSection}

              {/* Platform Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platforms (Select Multiple)
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {platformOptions.map((platform) => (
                    <button
                      key={platform.value}
                      onClick={() => handlePlatformToggle(platform.value)}
                      className={`flex items-center justify-center p-3 rounded-lg border ${
                        selectedPlatforms.includes(platform.value)
                          ? 'bg-primary-50 border-primary-500 text-primary-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path d={platform.icon} fill="currentColor" />
                      </svg>
                      {platform.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Brand Voice */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Voice
                </label>
                <select
                  value={brandVoice}
                  onChange={(e) => setBrandVoice(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {voiceOptions.map((voice) => (
                    <option key={voice.value} value={voice.value}>
                      {voice.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Content Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content Type
                </label>
                <select
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {contentTypeOptions.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience
                </label>
                <input
                  type="text"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder="e.g., Tech professionals, Healthcare providers, etc."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Key Objectives */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Key Objectives
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {objectiveOptions.map((objective) => (
                    <button
                      key={objective.value}
                      onClick={() => handleObjectiveToggle(objective.value)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                        keyObjectives.includes(objective.value)
                          ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                          : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {objective.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Hashtags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Hashtags
                </label>
                <input
                  type="text"
                  value={customHashtags}
                  onChange={(e) => setCustomHashtags(e.target.value)}
                  placeholder="#YourBrand #Industry #Campaign"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Auto-Post Toggle */}
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Auto-Post</h3>
                  <p className="text-xs text-gray-500">Automatically post to connected platforms</p>
                </div>
                <button
                  onClick={() => setAutoPost(!autoPost)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    autoPost ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      autoPost ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {autoPost && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule Post
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              )}

              <button
                onClick={generatePost}
                disabled={isGenerating || isPosting}
                className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                  isGenerating || isPosting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {isGenerating ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating...
                  </div>
                ) : isPosting ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Posting...
                  </div>
                ) : (
                  'Generate Post'
                )}
              </button>
            </div>

            {/* Right Column - Generated Posts */}
            <div>
              <div className="space-y-6">
                {generatedPosts.length > 0 ? (
                  generatedPosts.map(renderPost)
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No posts generated</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started by creating your first social media post.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <QuickStart guides={quickStartGuides} />
      <DeploymentOptions appId="social-media-manager" position="none" />
    </div>
  );
}

export default SocialMediaManager; 